import { useAppStore } from '../stores/useAppStore'

interface ApiRequestOptions extends RequestInit {
  requireAuth?: boolean
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = useAppStore.getState().token
    
    if (!token) {
      throw new Error('No authentication token available')
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  private getHeaders(requireAuth: boolean = true): HeadersInit {
    const baseHeaders: HeadersInit = {
      'Content-Type': 'application/json'
    }

    if (requireAuth) {
      const authHeaders = this.getAuthHeaders()
      return { ...baseHeaders, ...authHeaders }
    }

    return baseHeaders
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      // Token expired or invalid - logout user
      useAppStore.getState().logout()
      useAppStore.getState().addNotification({
        type: 'error',
        message: 'Session expired. Please log in again.',
        timestamp: Date.now()
      })
      throw new Error('Authentication failed')
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  }

  async get<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(requireAuth),
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }

  async post<T>(url: string, data?: unknown, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(requireAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }

  async put<T>(url: string, data?: unknown, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(requireAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }

  async patch<T>(url: string, data?: unknown, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getHeaders(requireAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }

  async delete<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(requireAuth),
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }

  // Special method for file uploads
  async uploadFile<T>(url: string, formData: FormData, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options
    
    // Check if token is expired before making request
    if (requireAuth && useAppStore.getState().isTokenExpired()) {
      useAppStore.getState().logout()
      throw new Error('Token expired')
    }

    const token = useAppStore.getState().token
    const headers: HeadersInit = {}
    
    if (requireAuth && token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      ...fetchOptions
    })

    return this.handleResponse<T>(response)
  }
}

export const apiService = new ApiService()
export default apiService 